/**
 * Every game this device has ever saved (`GameStore.listAll()`), newest
 * first — reachable from Mode Select and from the Final Results screen.
 * Tapping a finished game reopens its Final Results read-only (see
 * `renderSummary`'s `historyBack` param); tapping an unfinished one resumes
 * it exactly like the normal "continue where you left off" flow, since nothing
 * about "this game was left mid-way" is specific to having browsed here.
 *
 * Also supports searching (by player name, table, or date — client-side,
 * everything's already loaded) and deleting one or more games at once, for
 * cleaning up test/practice games without deleting them one at a time.
 * "Select All" only ever selects the games currently shown, and narrowing the
 * search drops any hidden game from the selection — you only delete what you
 * can see.
 */

import { summarise } from '../../game/summary';
import type { Game } from '../../game/types';
import { renderBrandHeader } from '../branding';
import { accuracyHeadlines, type EngineAccuracy } from '../recognitionAccuracy';
import type { RecognitionEngine } from '../recognitionEngine';
import { esc, formatGameTimestamp, matchesSearch, playerName, toggleSelectAll } from '../helpers';
import { updateMarker } from '../updateSafety';

export interface PastGamesActions {
  open(gameId: string): void;
  back(): void;
  /** Permanently deletes every listed game id — the caller re-renders this screen with the refreshed list afterward. */
  deleteGames(gameIds: string[]): void;
  /** Saves every game on this device as one file to keep somewhere safe; resolves with the line to show. */
  saveBackup(): Promise<string>;
  /** Reads a game file the person picked and restores the games in it; the caller re-renders this screen with the result as `notice`. */
  restoreBackup(file: File): void;
}

function statusLine(game: Game): string {
  if (!game.endedAt) {
    return `In progress · ${game.rounds.length} round${game.rounds.length === 1 ? '' : 's'} so far`;
  }
  const summary = summarise(game);
  const winnerNames = summary.winners.map((id) => playerName(game, id)).join(', ');
  const label = summary.winners.length > 1 ? 'Tied' : 'Winner';
  return `${label}: ${esc(winnerNames)} · ${summary.roundsPlayed} round${summary.roundsPlayed === 1 ? '' : 's'}`;
}

/** Everything a search query might reasonably match against, lowercased once per game rather than per keystroke. */
function searchBlob(game: Game): string {
  const parts = [
    game.tableId,
    formatGameTimestamp(game.startedAt),
    ...game.players.map((p) => p.name),
    game.endedAt ? formatGameTimestamp(game.endedAt) : 'in progress',
  ];
  return parts.join('   ').toLowerCase();
}

/** The header line under the title: how well tile recognition has been doing on this device. */
function accuracyBanner(accuracy: Record<RecognitionEngine, EngineAccuracy> | undefined): string {
  if (!accuracy) return '';
  const lines = accuracyHeadlines(accuracy);
  if (lines.length === 0) {
    return `<div class="accuracy-note" id="pastGamesAccuracy">
      <strong>Tile recognition accuracy</strong>
      <p class="muted">No scanned hands saved yet. Once a hand read from a photo is saved as a round, how many tiles it got right shows here.</p>
    </div>`;
  }
  return `<div class="accuracy-note" id="pastGamesAccuracy">
    <strong>Tile recognition accuracy</strong>
    ${lines.map((l) => `<p><span class="accuracy-engine">${esc(l.label)}</span> ${esc(l.text)}</p>`).join('')}
    <p class="muted">This device only, from hands saved after a photo scan.</p>
  </div>`;
}

export function renderPastGames(
  root: HTMLElement,
  games: Game[],
  actions: PastGamesActions,
  notice?: string,
  accuracy?: Record<RecognitionEngine, EngineAccuracy>,
): void {
  const rows = games
    .map(
      (g) => `
      <div class="past-game-item" data-game-id="${esc(g.id)}" data-search="${esc(searchBlob(g))}">
        <input type="checkbox" class="past-game-select" data-game-id="${esc(g.id)}" hidden style="flex-shrink:0;width:1.25rem;height:1.25rem" />
        <button type="button" class="secondary past-game-row" data-game-id="${esc(g.id)}" style="flex:1;min-width:0;text-align:left">
          <div style="font-weight:600">Table ${esc(g.tableId)}</div>
          <div class="muted" style="margin-top:0.15rem">${formatGameTimestamp(g.startedAt)}</div>
          <div class="muted" style="margin-top:0.15rem">${statusLine(g)}</div>
        </button>
      </div>
    `,
    )
    .join('');

  root.innerHTML = `
    ${renderBrandHeader()}
    ${updateMarker('ok')}
    <div class="card">
      <h1>Past Games</h1>
      ${notice ? `<p class="notice" id="pastGamesNotice" role="status">${esc(notice)}</p>` : ''}
      ${accuracyBanner(accuracy)}
      ${
        games.length === 0
          ? `<p class="muted">No games recorded on this device yet.</p>`
          : `
            <input
              type="search"
              id="pastGamesSearch"
              enterkeyhint="search"
              autocomplete="off"
              placeholder="Search by player name, table, or date…"
              style="width:100%;margin-bottom:0.75rem"
            />
            <button type="button" id="selectModeBtn" class="secondary" style="width:100%;margin-bottom:0.5rem">
              🗑️ Select Games to Delete
            </button>
            <div id="selectControls" hidden>
              <div class="row" style="margin-bottom:0.5rem">
                <button type="button" id="selectAllBtn" class="secondary">Select All</button>
                <button type="button" id="cancelSelectBtn" class="ghost">Cancel</button>
              </div>
              <button type="button" id="deleteSelectedBtn" class="danger" style="width:100%;margin-bottom:0.5rem">Delete Selected</button>
            </div>
            <div id="deleteConfirm"></div>
            <p id="searchCount" class="muted" style="margin:0 0 0.5rem" hidden></p>
            <p id="noMatchMsg" class="muted" hidden>No games match your search.</p>
            <div class="stack" id="pastGamesList">${rows}</div>
          `
      }
      <div class="backup-panel">
        <h2>Keep your games safe</h2>
        <p class="muted">Games are stored only on this device. If the phone is lost, reset, or its browser data is
        cleared, they are gone. Save them to a file now and then, and keep it somewhere else.</p>
        ${games.length > 0 ? `<button type="button" id="saveBackupBtn" class="secondary" style="width:100%;margin-bottom:0.5rem">💾 Save All Games to a File</button>` : ''}
        <button type="button" id="restoreBackupBtn" class="secondary" style="width:100%">📂 Restore Games from a File</button>
        <input type="file" id="restoreBackupInput" accept=".json,application/json" hidden />
        <p id="backupMsg" class="muted" role="status" hidden></p>
      </div>
      <button type="button" id="pastGamesBackBtn" class="ghost" style="width:100%;margin-top:1rem">← Back</button>
    </div>
  `;

  root.querySelector<HTMLButtonElement>('#pastGamesBackBtn')!.addEventListener('click', () => actions.back());
  wireBackup(root, actions);
  if (games.length === 0) return;

  const list = root.querySelector<HTMLDivElement>('#pastGamesList')!;
  const items = [...list.querySelectorAll<HTMLDivElement>('.past-game-item')];
  const searchInput = root.querySelector<HTMLInputElement>('#pastGamesSearch')!;
  const noMatchMsg = root.querySelector<HTMLParagraphElement>('#noMatchMsg')!;
  const searchCount = root.querySelector<HTMLParagraphElement>('#searchCount')!;
  const selectModeBtn = root.querySelector<HTMLButtonElement>('#selectModeBtn')!;
  const selectControls = root.querySelector<HTMLDivElement>('#selectControls')!;
  const selectAllBtn = root.querySelector<HTMLButtonElement>('#selectAllBtn')!;
  const cancelSelectBtn = root.querySelector<HTMLButtonElement>('#cancelSelectBtn')!;
  const deleteSelectedBtn = root.querySelector<HTMLButtonElement>('#deleteSelectedBtn')!;
  const deleteConfirm = root.querySelector<HTMLDivElement>('#deleteConfirm')!;

  let selecting = false;
  const selected = new Set<string>();

  const idOf = (item: HTMLElement): string => item.dataset['gameId']!;
  const checkboxOf = (item: HTMLElement): HTMLInputElement => item.querySelector<HTMLInputElement>('.past-game-select')!;
  const visibleItems = (): HTMLDivElement[] => items.filter((item) => !item.hidden);

  function updateControls(): void {
    deleteSelectedBtn.textContent = selected.size > 0 ? `Delete Selected (${selected.size})` : 'Delete Selected';
    deleteSelectedBtn.disabled = selected.size === 0;

    const visible = visibleItems();
    const allSelected = visible.length > 0 && visible.every((item) => selected.has(idOf(item)));
    selectAllBtn.textContent = allSelected ? 'Deselect All' : searchInput.value.trim() ? 'Select All Shown' : 'Select All';
    selectAllBtn.disabled = visible.length === 0;
  }

  function applyFilter(): void {
    const query = searchInput.value.trim().toLowerCase();
    let anyVisible = false;
    let matchCount = 0;
    for (const item of items) {
      const matches = matchesSearch(item.dataset['search'] ?? '', query);
      item.hidden = !matches;
      if (matches) {
        anyVisible = true;
        matchCount++;
      }
      // A game you can no longer see must never stay selected for deletion.
      if (!matches && selected.delete(idOf(item))) checkboxOf(item).checked = false;
    }
    noMatchMsg.hidden = anyVisible;
    // Say what the search did, so pressing Enter (or just typing) visibly does something.
    searchCount.hidden = !query || !anyVisible;
    searchCount.textContent = `${matchCount} of ${items.length} games match`;
    // Any confirmation on screen was for the old selection.
    deleteConfirm.innerHTML = '';
    updateControls();
  }

  function setSelecting(next: boolean): void {
    selecting = next;
    selected.clear();
    deleteConfirm.innerHTML = '';
    for (const item of items) {
      const checkbox = checkboxOf(item);
      checkbox.hidden = !next;
      checkbox.checked = false;
    }
    selectModeBtn.hidden = next;
    selectControls.hidden = !next;
    updateControls();
  }

  searchInput.addEventListener('input', applyFilter);
  // The list already filters as you type, so Enter has nothing left to compute —
  // but on a phone it should still answer: close the keyboard so the results are
  // in view (and re-apply, in case a browser autofilled without an input event).
  searchInput.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    applyFilter();
    searchInput.blur();
  });

  selectModeBtn.addEventListener('click', () => setSelecting(true));
  cancelSelectBtn.addEventListener('click', () => setSelecting(false));

  selectAllBtn.addEventListener('click', () => {
    const next = toggleSelectAll(visibleItems().map(idOf), selected);
    selected.clear();
    for (const id of next) selected.add(id);
    for (const item of items) checkboxOf(item).checked = selected.has(idOf(item));
    deleteConfirm.innerHTML = '';
    updateControls();
  });

  for (const item of items) {
    const gameId = idOf(item);
    const checkbox = checkboxOf(item);
    const openBtn = item.querySelector<HTMLButtonElement>('.past-game-row')!;

    checkbox.addEventListener('change', () => {
      if (checkbox.checked) selected.add(gameId);
      else selected.delete(gameId);
      deleteConfirm.innerHTML = '';
      updateControls();
    });

    // While selecting, tapping the row toggles its checkbox instead of
    // opening the game — checking `selecting` here (rather than swapping
    // click handlers) keeps this a single listener attached once at render
    // time, not something to rewire every time selection mode is toggled.
    openBtn.addEventListener('click', () => {
      if (selecting) {
        checkbox.checked = !checkbox.checked;
        checkbox.dispatchEvent(new Event('change'));
        return;
      }
      actions.open(gameId);
    });
  }

  deleteSelectedBtn.addEventListener('click', () => {
    if (selected.size === 0) return;
    const ids = [...selected];
    deleteSelectedBtn.disabled = true;
    deleteConfirm.innerHTML = `
      <div class="card" style="margin-top:0.5rem">
        <p><strong>Delete ${ids.length} game${ids.length === 1 ? '' : 's'}?</strong></p>
        <p class="muted">This removes ${ids.length === 1 ? 'it' : 'them'} from this device entirely, including every
        round recorded. This can't be undone.</p>
        <div class="row">
          <button type="button" id="deleteConfirmCancelBtn" class="secondary">Cancel</button>
          <button type="button" id="deleteConfirmYesBtn" class="danger">Yes, Delete</button>
        </div>
      </div>
    `;
    deleteConfirm.querySelector<HTMLButtonElement>('#deleteConfirmCancelBtn')!.addEventListener('click', () => {
      deleteConfirm.innerHTML = '';
      deleteSelectedBtn.disabled = false;
    });
    deleteConfirm.querySelector<HTMLButtonElement>('#deleteConfirmYesBtn')!.addEventListener('click', () => {
      actions.deleteGames(ids);
    });
  });
}

function wireBackup(root: HTMLElement, actions: PastGamesActions): void {
  const saveBtn = root.querySelector<HTMLButtonElement>('#saveBackupBtn');
  const restoreBtn = root.querySelector<HTMLButtonElement>('#restoreBackupBtn')!;
  const input = root.querySelector<HTMLInputElement>('#restoreBackupInput')!;
  const msg = root.querySelector<HTMLParagraphElement>('#backupMsg')!;

  saveBtn?.addEventListener('click', () => {
    saveBtn.disabled = true;
    void actions
      .saveBackup()
      .then((line) => {
        msg.textContent = line;
      })
      .catch(() => {
        msg.textContent = "The file couldn't be saved. Try again.";
      })
      .finally(() => {
        msg.hidden = false;
        saveBtn.disabled = false;
      });
  });

  restoreBtn.addEventListener('click', () => input.click());
  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (!file) return;
    restoreBtn.disabled = true;
    msg.textContent = 'Reading the file…';
    msg.hidden = false;
    actions.restoreBackup(file);
  });
}
